// Deps
import { useRouter } from "next/router";
import { getResourceSlugFromPath } from "~/modules/link-parser";
import { createComponent } from "react-fela";
import { colors, gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import get from "lodash.get";

import { useContext } from "react";
import {
    ResourceContext,
    TaxonomyContext,
    AuthorContext
} from "~/context/content";

import Icon from "~/components/shared/icon";
import Link from "next/link";

const PAGE_COUNT = 10;

export default function Pagination({ paging }) {
    if (!paging || paging.totalPages <= 1) return null;

    // Setup needed data
    const { asPath, query } = useRouter();
    const type = useContext(ResourceContext);
    const author = useContext(AuthorContext);
    const slug = getResourceSlugFromPath(asPath);
    const taxonomy = useContext(TaxonomyContext);
    const routeBase = getPaginationBase({ type, taxonomy, slug, author });

    const currentPage = parseInt(get(query, "page", 1));
    const previousPage = Math.max(currentPage - 1, 1);
    const nextPage = Math.min(currentPage + 1, paging.totalPages);

    const lowerRange = Math.max(currentPage - Math.round(PAGE_COUNT / 2), 1);

    return (
        <Container>
            {/* Previous Page arrrow */}
            {currentPage !== 1 && (
                <LinkWrapper page={previousPage} routeBase={routeBase}>
                    <PaginationButton>
                        <Icon
                            name="pagination-button"
                            flipped={true}
                        />
                    </PaginationButton>
                </LinkWrapper>
            )}

            {Array.apply(null, { length: PAGE_COUNT }).map((_, i) => {
                let page = lowerRange + i;
                if (page > paging.totalPages) return null;

                return (
                    <LinkWrapper page={page} routeBase={routeBase} key={i}>
                        <PageButton active={currentPage == page}>
                            {page}
                        </PageButton>
                    </LinkWrapper>
                );
            })}

            {/* Next Page arrrow */}
            {currentPage !== paging.totalPages && (
                <LinkWrapper page={nextPage} routeBase={routeBase}>
                    <PaginationButton>
                        <Icon name="pagination-button" />
                    </PaginationButton>
                </LinkWrapper>
            )}
        </Container>
    );
}

const LinkWrapper = ({ routeBase, page, children }) => {
    const link = page == 1 ? "/" : `/page/${page}`;
    if (process.env.NEXT_MODE === "spa")
        return (
            <Link
                as={`${routeBase.as}${link}`}
                href={`${routeBase.href}/page/[page]`}
            >
                <a>{children}</a>
            </Link>
        );
    else return <a href={`${routeBase.as}${link}`}>{children}</a>;
};

/**
 * Determine how to route the pagination links
 */
const getPaginationBase = ({ slug, type, taxonomy, author }) => {
    if (author && author.slug) {
        return {
            as: `/blog/author/${author.slug}`,
            href: `/blog/author/[slug]`
        };
    }

    if (taxonomy && taxonomy.slug) {
        return {
            as: `/blog/${taxonomy.type}/${taxonomy.slug}`,
            href: `/blog/${taxonomy.type}/[slug]`
        };
    }

    switch (type) {
        default:
            return {
                as: `/${slug}`,
                href: `/${slug}`
            };
    }
};

const Container = createComponent(() => ({
    textAlign: "center",

    extend: [
        fluid("paddingH", gutters.default),
        fluid("paddingV", { max: 75, min: 40 })
    ]
}));

const hoverStates = {
    color: colors.black,
    willChange: "color, border",
    transition: "color 0.2s ease, border 0.2s ease",

    ":hover": {
        color: colors.blue,
        borderColor: colors.blue
    }
};

const PageButton = createComponent(
    ({ active }) => ({
        fontSize: 18,
        ...hoverStates,
        extend: [
            fluid("paddingH", { max: 20, min: 6 }),

            {
                condition: active,
                style: {
                    color: colors.blue
                }
            }
        ]
    }),
    "span"
);

const PaginationButton = createComponent(
    () => ({
        ...hoverStates,

        extend: [
            fluid("marginH", { max: 20, min: 8 }),
        ],

        maxWidthMobile: {
            marginH: 0
        }
    }),
    "span"
);
